/**
 * useGyms Hook
 *
 * Custom hook for querying gym data from Firestore.
 * Supports fetching all gyms, searching by name, and fetching a single gym.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  collection,
  query,
  getDocs,
  doc,
  getDoc,
  orderBy,
  type QuerySnapshot,
  type DocumentData,
} from 'firebase/firestore';
import { getFirebaseDb } from '@/lib/firebase';
import type { Gym } from '@flash/shared';

/**
 * Convert Firestore document to Gym type
 * Note: Document ID must be passed separately as it's not in the document data
 */
const docToGym = (docId: string, docData: DocumentData): Gym => {
  const gradingSystem = docData['gradingSystem'] as Gym['gradingSystem'] | undefined;
  const routeRequirements = docData['routeRequirements'] as Gym['routeRequirements'] | undefined;
  const allowGradeModifiers = docData['allowGradeModifiers'] as boolean | undefined;
  const labelingMethods = docData['labelingMethods'] as Gym['labelingMethods'] | undefined;
  const tagColorGrades = docData['tagColorGrades'] as Gym['tagColorGrades'] | undefined;

  return {
    id: docId,
    name: docData['name'] as string,
    description: (docData['description'] as string | null) ?? null,
    address: docData['address'] as string,
    city: docData['city'] as string,
    state: docData['state'] as string,
    postalCode: (docData['postalCode'] as string | null) ?? null,
    country: docData['country'] as string,
    logoURL: (docData['logoURL'] as string | null) ?? null,
    colorGrades: docData['colorGrades'] as Gym['colorGrades'],
    floorPlan: (docData['floorPlan'] as Gym['floorPlan']) ?? null,
    createdAt: docData['createdAt'],
    updatedAt: docData['updatedAt'],
    // Spread optional properties only if they have values
    ...(gradingSystem && { gradingSystem }),
    ...(routeRequirements && { routeRequirements }),
    ...(allowGradeModifiers !== undefined && { allowGradeModifiers }),
    ...(labelingMethods && { labelingMethods }),
    ...(tagColorGrades && { tagColorGrades }),
  };
};

/**
 * State interface for gym queries
 */
interface UseGymsState {
  /** List of gyms (all or filtered) */
  gyms: Gym[];
  /** Whether data is being loaded */
  loading: boolean;
  /** Error message if query failed */
  error: string | null;
}

/**
 * Return type for useGyms hook
 */
interface UseGymsResult extends UseGymsState {
  /** Refetch gyms from Firestore */
  refetch: () => Promise<void>;
  /** Search gyms by name (client-side filtering) */
  searchGyms: (searchTerm: string) => Gym[];
}

/**
 * Hook to fetch all gyms from Firestore
 *
 * @returns Object containing gyms array, loading state, error, and utility functions
 *
 * @example
 * ```tsx
 * const { gyms, loading, error, searchGyms } = useGyms();
 *
 * if (loading) return <LoadingSpinner />;
 * if (error) return <ErrorMessage message={error} />;
 *
 * const filteredGyms = searchTerm ? searchGyms(searchTerm) : gyms;
 * return <GymList gyms={filteredGyms} />;
 * ```
 */
export const useGyms = (): UseGymsResult => {
  const [state, setState] = useState<UseGymsState>({
    gyms: [],
    loading: true,
    error: null,
  });

  /**
   * Fetch all gyms from Firestore
   */
  const fetchGyms = useCallback(async (): Promise<void> => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const db = getFirebaseDb();
      const gymsRef = collection(db, 'gyms');
      const gymsQuery = query(gymsRef, orderBy('name', 'asc'));

      const snapshot: QuerySnapshot = await getDocs(gymsQuery);

      const gyms: Gym[] = snapshot.docs.map((docSnapshot) =>
        docToGym(docSnapshot.id, docSnapshot.data())
      );

      setState({ gyms, loading: false, error: null });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch gyms';
      console.error('Error fetching gyms:', err);
      setState((prev) => ({ ...prev, loading: false, error: errorMessage }));
    }
  }, []);

  /**
   * Search gyms by name (case-insensitive, client-side filtering)
   */
  const searchGyms = useCallback(
    (searchTerm: string): Gym[] => {
      if (!searchTerm.trim()) {
        return state.gyms;
      }

      const normalizedTerm = searchTerm.toLowerCase().trim();

      return state.gyms.filter(
        (gym) =>
          gym.name.toLowerCase().includes(normalizedTerm) ||
          gym.city.toLowerCase().includes(normalizedTerm) ||
          gym.state.toLowerCase().includes(normalizedTerm)
      );
    },
    [state.gyms]
  );

  // Fetch gyms on mount
  useEffect(() => {
    fetchGyms();
  }, [fetchGyms]);

  return {
    ...state,
    refetch: fetchGyms,
    searchGyms,
  };
};

/**
 * State interface for single gym query
 */
interface UseGymState {
  /** The gym document (null if not found) */
  gym: Gym | null;
  /** Whether data is being loaded */
  loading: boolean;
  /** Error message if query failed */
  error: string | null;
}

/**
 * Return type for useGym hook
 */
interface UseGymResult extends UseGymState {
  /** Refetch gym from Firestore */
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch a single gym by ID from Firestore
 *
 * @param gymId - The gym document ID to fetch
 * @returns Object containing gym, loading state, error, and refetch function
 *
 * @example
 * ```tsx
 * const { gym, loading, error } = useGym(gymId);
 *
 * if (loading) return <LoadingSpinner />;
 * if (error) return <ErrorMessage message={error} />;
 * if (!gym) return <NotFound />;
 *
 * return <GymDetail gym={gym} />;
 * ```
 */
export const useGym = (gymId: string | undefined): UseGymResult => {
  const [state, setState] = useState<UseGymState>({
    gym: null,
    loading: true,
    error: null,
  });

  /**
   * Fetch single gym from Firestore
   */
  const fetchGym = useCallback(async (): Promise<void> => {
    if (!gymId) {
      setState({ gym: null, loading: false, error: 'No gym ID provided' });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const db = getFirebaseDb();
      const gymRef = doc(db, 'gyms', gymId);
      const gymSnapshot = await getDoc(gymRef);

      if (!gymSnapshot.exists()) {
        setState({ gym: null, loading: false, error: null });
        return;
      }

      const gym = docToGym(gymSnapshot.id, gymSnapshot.data());
      setState({ gym, loading: false, error: null });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch gym';
      console.error('Error fetching gym:', err);
      setState((prev) => ({ ...prev, loading: false, error: errorMessage }));
    }
  }, [gymId]);

  // Fetch gym when gymId changes
  useEffect(() => {
    fetchGym();
  }, [fetchGym]);

  return {
    ...state,
    refetch: fetchGym,
  };
};

/**
 * Memoized gym lookup by ID (useful when you already have gyms loaded)
 *
 * @param gyms - Array of gyms
 * @param gymId - The gym ID to find
 * @returns The gym if found, undefined otherwise
 */
export const useGymById = (gyms: Gym[], gymId: string | undefined): Gym | undefined => {
  return useMemo(() => {
    if (!gymId) return undefined;
    return gyms.find((gym) => gym.id === gymId);
  }, [gyms, gymId]);
};
